/**
 * Normalize a risk row from the API into the shape the UI expects.
 * The backend ships snake_case keys; this function reads them as-is and exposes
 * a camelCase alias for each one so consuming components can read either form.
 */
export function normalizeRiskRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const read = (key) => {
    const v = raw[key];
    return v === undefined || v === null || v === '' ? undefined : v;
  };

  const id = read('id') ?? read('risk_id');
  const riskId = read('risk_id');
  const riskLevel = read('risk_level');
  const riskDescription = read('risk_description');
  const riskCategory = read('risk_category');
  const recommendation = read('recommendation');
  const classificationRaw = String(read('classification') ?? 'business').trim().toLowerCase();
  const classification = classificationRaw === 'itgc' ? 'itgc' : 'business';
  const itgcDomainId = read('itgc_domain_id') ?? read('itgcDomainId');
  const itgcDomain = read('itgc_domain') ?? read('itgcDomain');
  const itgcDomainCode =
    read('itgc_domain_code') ??
    read('itgcDomainCode') ??
    (itgcDomain && typeof itgcDomain === 'object' ? itgcDomain.code : undefined);
  const sourceRef = read('source_ref') ?? read('sourceRef');
  const sourceRefVersionNumber =
    read('source_ref_version_number') ?? read('sourceRefVersionNumber');
  const sourceRefVersionStatus =
    read('source_ref_version_status') ?? read('sourceRefVersionStatus');
  const processId = read('process_id');
  const nodeId = read('node_id');
  const cycleId = read('cycle_id');
  const processName = read('process_name');
  const nodeDescription = read('node_description');
  const cycleName = read('cycle_name');

  return {
    ...raw,
    id,
    riskId,
    risk_id: riskId,
    riskLevel,
    risk_level: riskLevel,
    riskDescription,
    risk_description: riskDescription,
    riskCategory,
    risk_category: riskCategory,
    recommendation,
    classification,
    itgcDomainId,
    itgc_domain_id: itgcDomainId,
    itgcDomain,
    itgc_domain: itgcDomain,
    itgcDomainCode,
    itgc_domain_code: itgcDomainCode,
    sourceRef,
    source_ref: sourceRef,
    sourceRefVersionNumber,
    source_ref_version_number: sourceRefVersionNumber,
    sourceRefVersionStatus,
    source_ref_version_status: sourceRefVersionStatus,
    processId,
    process_id: processId,
    nodeId,
    node_id: nodeId,
    cycleId,
    cycle_id: cycleId,
    processName,
    process_name: processName,
    nodeDescription,
    node_description: nodeDescription,
    cycleName,
    cycle_name: cycleName,
  };
}

export function normalizeRiskRows(arr) {
  return Array.isArray(arr) ? arr.map(normalizeRiskRow).filter(Boolean) : [];
}

/**
 * Extract a raw risk array from any of the response shapes we've seen:
 *   - `[]` (raw array)
 *   - `{ risks: [] }` (legacy)
 */
export function extractRisksArray(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === 'object') {
    if (Array.isArray(body.items)) return body.items;
    if (Array.isArray(body.risks)) return body.risks;
    if (Array.isArray(body.data)) return body.data;
  }
  return [];
}

/** Process rows for list UI: `processes[]` or, when absent, `process_mappings[]` (same shape). */
function getRiskProcessRowsForList(risk) {
  if (Array.isArray(risk?.processes) && risk.processes.length > 0) {
    return risk.processes;
  }
  const mapped = risk?.process_mappings ?? risk?.processMappings;
  if (Array.isArray(mapped) && mapped.length > 0) {
    return mapped;
  }
  return [];
}

/** Processes column popover entries — `GET /api/risks` embeds `processes[]` or `process_mappings[]`. */
export function riskProcessAssociations(risk) {
  const rows = getRiskProcessRowsForList(risk);
  return rows
    .map((p) => {
      const processId = p.process_id ?? p.processId;
      const cycleId = p.cycle_id ?? p.cycleId;
      const processName = p.process_name ?? p.processName ?? '';
      const processDisplayId =
        processId != null && processId !== '' ? `${String(cycleId)} - ${String(processId)}` : `${String(cycleId)} - ${String(p.id ?? '')}`;
      const slug =
        processId != null && processId !== ''
          ? String(processId)
          : processName || String(p.id ?? '');
      return {
        id: slug,
        processId:
          processId != null && processId !== '' ? processId : undefined,
        cycleId: cycleId != null && cycleId !== '' ? cycleId : undefined,
        processDisplayId,
        processName: processName ? String(processName) : ''
      };
    })
    .filter((x) => (x.processDisplayId != null && x.processDisplayId !== '') || x.processName);
}

export function riskProcessCount(risk) {
  const n = risk?.process_count;
  if (typeof n === 'number' && Number.isFinite(n)) return n;
  return riskProcessAssociations(risk).length;
}

/** Process rows with nested `controls[]` from `process_mappings` or `processes`. */
function getRiskProcessMappingRows(risk) {
  const raw = risk?.process_mappings ?? risk?.processMappings ?? risk?.processes ?? risk?.Processes;
  return Array.isArray(raw) ? raw : [];
}

/**
 * Wire format may ship full `controls[]` or only `control_ids` / `controlIds` per process row.
 */
function controlsFromProcessMappingRow(p, rowIndex) {
  const nested = Array.isArray(p.controls) ? p.controls : [];
  if (nested.length > 0) {
    return nested.map((c, i) => ({
      id: String(c.id ?? i),
      controlId: c.id,
      controlDisplayId: String(c.control_display_id ?? c.controlDisplayId ?? '').trim() || 'Control',
      controlTitle: String(c.control_title ?? c.controlTitle ?? '').trim(),
    }));
  }
  const idList = p.control_ids ?? p.controlIds;
  if (!Array.isArray(idList) || idList.length === 0) return [];
  return idList.map((rawId, i) => {
    const sid = rawId != null ? String(rawId).trim() : '';
    return {
      id: sid || `control-id-${rowIndex}-${i}`,
      controlId: rawId,
      controlDisplayId: sid || 'Control',
      controlTitle: '',
    };
  });
}

function countControlsInProcessMappingRow(p) {
  const nested = Array.isArray(p.controls) ? p.controls : [];
  if (nested.length > 0) return nested.length;
  const idList = p.control_ids ?? p.controlIds;
  return Array.isArray(idList) ? idList.length : 0;
}

/**
 * Controls grouped by process for the RCM popover (one block per process pairing).
 */
export function riskControlAssociationGroups(risk) {
  const rows = getRiskProcessMappingRows(risk);
  if (rows.length > 0) {
    return rows
      .map((p, index) => {
        const processId = String(p.process_id ?? p.processId ?? '').trim();
        const processName = String(p.process_name ?? p.processName ?? '').trim();
        const cycleName = String(p.cycle_name ?? p.cycleName ?? '').trim();
        const cycleId = String(p.cycle_id ?? p.cycleId ?? '').trim();
        const id = String(p.id ?? p.process_uuid ?? `process-block-${index}`).trim();
        const controls = controlsFromProcessMappingRow(p, index);
        return {
          id,
          processId,
          processName,
          cycleName,
          cycleId,
          controls,
        };
      })
      .filter((g) => g.processName || g.processId || g.cycleName || g.cycleId || g.controls.length > 0);
  }

  const flat = riskControlAssociations(risk);
  if (flat.length === 0) return [];
  return [
    {
      id: 'legacy-flat-controls',
      processId: '',
      processName: '',
      cycleName: '',
      cycleId: '',
      controls: flat.map((f) => ({
        id: f.id,
        controlId: f.controlId,
        controlDisplayId: f.controlDisplayId,
        controlTitle: f.controlTitle,
      })),
    },
  ];
}

/** Controls column popover entries — each risk row embeds `controls[]` (flat legacy). */
export function riskControlAssociations(risk) {
  const rows = Array.isArray(risk?.controls) ? risk.controls : [];
  return rows.map((c, i) => ({
    id: String(c.id ?? i),
    controlId: c.id,
    controlDisplayId: c.control_display_id ?? c.controlDisplayId ?? 'Control',
    controlTitle: c.control_title ?? c.controlTitle ?? '',
    isAIRecommendation: Boolean(c.is_ai_recommendation ?? c.isAIRecommendation),
  }));
}

export function riskControlCount(risk) {
  const n = risk?.control_count;
  if (typeof n === 'number' && Number.isFinite(n)) return n;
  const mappingRows = getRiskProcessMappingRows(risk);
  if (mappingRows.length > 0) {
    const fromMappings = mappingRows.reduce((sum, p) => sum + countControlsInProcessMappingRow(p), 0);
    if (fromMappings > 0) return fromMappings;
  }
  return riskControlAssociations(risk).length;
}

/** Discriminator for risks-table association columns (processes vs controls). */
export const RISK_ASSOCIATION_KIND = Object.freeze({
  PROCESS: 'process',
  CONTROL: 'control',
});

const associationByKind = {
  [RISK_ASSOCIATION_KIND.PROCESS]: {
    entries: riskProcessAssociations,
    count: riskProcessCount,
  },
  [RISK_ASSOCIATION_KIND.CONTROL]: {
    entries: riskControlAssociations,
    count: riskControlCount,
  },
};

export function riskAssociationEntries(risk, kind) {
  return associationByKind[kind]?.entries(risk) ?? [];
}

export function riskAssociationCount(risk, kind) {
  const fn = associationByKind[kind]?.count;
  return fn ? fn(risk) : 0;
}
